import React, { useState, useCallback, ReactElement } from 'react';
import * as css from './BatteryInformation.module.scss';
import 'src/css/themes.scss';
import { IconSeeMore } from 'src/content/icons';
import { ConnectedVehicleRec } from 'src/models';
import { Dialog, ScrollBar } from '@framework-controls/core';
import { strings } from 'src/localization';

export interface IProps {
    connectedVehicle: ConnectedVehicleRec;
}

const BatteryInformation = (props: IProps) => {
    const [infoOpen, setInfoOpen] = useState(false);
    const toggleInfo = useCallback(() => {
        setInfoOpen(!infoOpen);
    }, [infoOpen]);

    let batteryIcon: ReactElement | null = null;
    if (props.connectedVehicle.battIcon) {
        batteryIcon =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Battery}
                </div>
                <iframe className={css.iframe} title='BatteryIcon' src={props.connectedVehicle.battIcon}></iframe>
            </span>;
    }

    let lastUpdated: ReactElement | null = null;
    if (props.connectedVehicle.battLastUpdTstamp) {
        lastUpdated =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.LastUpdated}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.battLastUpdTstamp}
                </div>
            </span>;
    }

    let batteryCharge: ReactElement | null = null;
    if (props.connectedVehicle.battLevel) {
        let statusLit = strings.DataAsOf;
        if (props.connectedVehicle.battActualStatus) {
            statusLit = props.connectedVehicle.battActualStatus + ' ' + strings.AsOf;
        }
        let batteryChargeTstamp: ReactElement | null = null;
        if (props.connectedVehicle.battTstamp) {
            batteryChargeTstamp =
                <p className={css.statusLit}>{statusLit + ' ' + props.connectedVehicle.battTstamp}</p>;
        }
        batteryCharge =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.BatteryCharge}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.battLevel}
                </div>
                {batteryChargeTstamp}
            </span>;
    }

    let batteryStatus: ReactElement | null = null;
    if (props.connectedVehicle.battStatus || props.connectedVehicle.battDesc) {
        let battStatus: ReactElement | null = null;
        if (props.connectedVehicle.battStatus) {
            battStatus =
                <div className={css.infoData}>
                    {props.connectedVehicle.battStatus}
                </div>;
        }
        let battDesc: ReactElement | null = null;
        if (props.connectedVehicle.battDesc) {
            battDesc =
            <div className={css.infoData}>
                {props.connectedVehicle.battDesc}
            </div>;
        }
        batteryStatus =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.BatteryStatus}
                </div>
                {battStatus}
                {battDesc}
            </span>;
    }

    let batteryHealthStatus: ReactElement | null = null;
    if (props.connectedVehicle.battHealth) {
        let batteryHealthTstamp: ReactElement | null = null;
        if (props.connectedVehicle.battHealthTstamp) {
            batteryHealthTstamp =
                <p className={css.statusLit}>{strings.DataAsOf + ' ' + props.connectedVehicle.battHealthTstamp}</p>;
        }
        batteryHealthStatus =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.BatteryHealthStatus}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.battHealth}
                </div>
                {batteryHealthTstamp}
            </span>;
    }

    let batteryChargePercent: ReactElement | null = null;
    if (props.connectedVehicle.battFillLevel) {
        let statusLit = strings.DataAsOf;
        if (props.connectedVehicle.battFillStatus) {
            statusLit = props.connectedVehicle.battFillStatus + ' ' + strings.AsOf;
        }
        let batteryChargeTstamp: ReactElement | null = null;
        if (props.connectedVehicle.battFillTstamp) {
            batteryChargeTstamp =
                <p className={css.statusLit}>{statusLit + ' ' + props.connectedVehicle.battFillTstamp}</p>;
        }
        batteryChargePercent =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.BatteryChargePercent}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.battFillLevel}
                </div>
                {batteryChargeTstamp}
            </span>;
    }

    let batteryDTE: ReactElement | null = null;
    if (props.connectedVehicle.battElecDte) {
        let statusLit = strings.DataAsOf;
        if (props.connectedVehicle.battElecStatus) {
            statusLit = props.connectedVehicle.battElecStatus + ' ' + strings.AsOf;
        }
        let batteryDTETstamp: ReactElement | null = null;
        if (props.connectedVehicle.battElecTstamp) {
            batteryDTETstamp =
                <p className={css.statusLit}>{statusLit + ' ' + props.connectedVehicle.battElecTstamp}</p>;
        }
        batteryDTE =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ElectricBatteryDTE}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.battElecDte}
                </div>
                {batteryDTETstamp}
            </span>;
    }

    return (
        <div className={css.block}>
            <div className={css.icon}>
                <IconSeeMore onClick={() => toggleInfo()} />
            </div>
            <Dialog
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={infoOpen}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={strings.BatteryInformation.toUpperCase()}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={() => toggleInfo()}
                onBackDropClick={() => toggleInfo()}
                width={'full'}
            >
                <ScrollBar scrollVertical>
                    <div className={css.infoRow}>
                        {batteryIcon}
                        {lastUpdated}
                        {batteryCharge}
                        {batteryStatus}
                        {batteryHealthStatus}
                        {batteryChargePercent}
                        {batteryDTE}
                    </div>
                </ScrollBar>
            </Dialog>
        </div>
    );
};

export default BatteryInformation;